import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { selectIsBusinessAccount } from 'src/app/state/my-account.selectors';
import { selectRegData } from 'src/app/state/reg-data.selectors';
import { CheckoutStepsRoute, NavigationRoute } from '../constants/constants';
import { BrandEnum, StepStatusEnum, TestTypeEnum } from '../dtos/enum';
import { RegData } from '../dtos/reg-data';
import { Step } from '../dtos/step';
import { ConfigHelper } from './helpers/config.helper';

@Injectable({
  providedIn: 'root'
})
export class StepProgressService {

  steps: Step[];
  regData: RegData;
  isBusinessAccount: boolean;

  private defaultSteps: Step[] = [
    { name: "Proof of Identity", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.proofOfIdentity, step: 1 },
    { name: "Score Report", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.scoreReport, step: 2 },
    { name: "Review & Add to Cart", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.testSummary, step: 3 },
    { name: "Study Materials", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.studyMaterials, step: 4 },
    { name: "Order Summary", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.orderSummary, step: 5 },
    { name: "Complete", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.orderComplete, step: 6 }
  ];

  private webDeliverySteps: Step[] = [
    { name: "Proof of Identity", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.proofOfIdentity, step: 1 },
    { name: "Photo", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.photoUpload, step: 2 },
    { name: "ID", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.scannedDocumentUpload, step: 3 },
    { name: "Score Report", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.scoreReport, step: 4 },
    { name: "Review & Add to Cart", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.testSummary, step: 5 },
    { name: "Study Materials", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.studyMaterials, step: 6 },
    { name: "Order Summary", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.orderSummary, step: 7 },
    { name: "Complete", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.orderComplete, step: 8 }
  ];

  private caelBusinessSteps: Step[] = [
    { name: "Select a sitting", status: StepStatusEnum.Pending, url: "batch-registration/" + NavigationRoute.REGISTRATION.sittingSelection, step: 1 },
    { name: "Select client accounts", status: StepStatusEnum.Pending, url: "batch-registration/" + NavigationRoute.REGISTRATION.accountSelection, step: 2 },
  ];

  private celpipBusinessSteps: Step[] = [
    { name: "Select a test", status: StepStatusEnum.Pending, url: "batch-registration/" + NavigationRoute.REGISTRATION.testSelection, step: 1 },
    { name: "Select a sitting", status: StepStatusEnum.Pending, url: "batch-registration/" + NavigationRoute.REGISTRATION.sittingSelection, step: 2 },
    { name: "Select client accounts", status: StepStatusEnum.Pending, url: "batch-registration/" + NavigationRoute.REGISTRATION.accountSelection, step: 3 }
  ];

  private saftSteps: Step[] = [
    { name: "Proof of Identity", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.proofOfIdentity, step: 1 },
    { name: "Review & Add to Cart", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.testSummary, step: 2 },
    { name: "Study Materials", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.studyMaterials, step: 3 },
    { name: "Order Summary", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.orderSummary, step: 4 },
    { name: "Complete", status: StepStatusEnum.Pending, url: "/registration/" + NavigationRoute.REGISTRATION.orderComplete, step: 5 }
  ];

  private checkoutSteps: Step[] = [
    { name: "Address", status: StepStatusEnum.Pending, url: CheckoutStepsRoute.CHECKOUT.adress, step: 1 },
    { name: "Shipping", status: StepStatusEnum.Pending, url: CheckoutStepsRoute.CHECKOUT.shipping, step: 2 },
    { name: "Review and Payment", status: StepStatusEnum.Pending, url: CheckoutStepsRoute.CHECKOUT.reviewAndPayment, step: 3 },
    { name: "Completed", status: StepStatusEnum.Pending, url: CheckoutStepsRoute.CHECKOUT.Completed, step: 4 },
  ];

  constructor(
    private store: Store,
    private configHelper: ConfigHelper,
    private router: Router,
  ) {
  }

  getSteps(currentStep: string, stepType?: StepTypeEnum) {
    combineLatest([this.store.pipe(select(selectRegData)), this.store.pipe(select(selectIsBusinessAccount))])
      .pipe(take(1)).subscribe(
        (result) => {
          this.regData = result[0];
          this.isBusinessAccount = result[1];
        });

    if (this.regData?.testSession?.isWebDelivery) {
      this.steps = this.webDeliverySteps;
    }
    else if (this.regData?.testSession?.testTypeId === TestTypeEnum.SAFT) {
      this.steps = this.saftSteps;
    }
    else {
      const brandId = this.configHelper.getBrandId();
      if (this.isBusinessAccount) {
        if (stepType && stepType === StepTypeEnum.Checkout) {
          this.steps = this.defaultSteps;
        } else {
          if (brandId === BrandEnum.CELPIP) {
            this.steps = this.celpipBusinessSteps;
          } else if (brandId === BrandEnum.CAEL) {
            this.steps = this.caelBusinessSteps;
          } else {
            console.log("Unknown brand");
          }
        }
      } else {
        this.steps = this.defaultSteps;
      }
    }
    const returnSteps = this.steps.slice(0);
    const currentStepNumber = this.steps.find(x => x.name === currentStep).step;
    returnSteps.forEach(element => {
      if (element.step < currentStepNumber) {
        element.status = StepStatusEnum.Completed;
      }
      else if (element.step === currentStepNumber) {
        element.status = StepStatusEnum.Active;
      }
      else {
        element.status = StepStatusEnum.Pending;
      }
    });
    return returnSteps;
  }

  getCheckoutSteps(currentStep: string) {
    this.steps = this.checkoutSteps;
    const currentStepNumber = this.steps.find(x => x.name === currentStep).step;
    this.steps.forEach(element => {
      if (element.step < currentStepNumber) {
        element.status = StepStatusEnum.Completed;
      }
      else if (element.step === currentStepNumber) {
        element.status = StepStatusEnum.Active;
      }
      else {
        element.status = StepStatusEnum.Pending;
      }
    });
    return this.steps;
  }

}

export enum PageStepEnum {
  ProofOfIdentity = "Proof of Identity",
  Photo = "Photo",
  ID = "ID",
  ScoreReport = "Score Report",
  ReviewAndAddToCart = "Review & Add to Cart",
  StudyMaterials = "Study Materials",
  OrderSummary = "Order Summary",
  Complete = "Complete",
  TestSelection = "Select a test",
  SittingSelection = "Select a sitting",
  ClientAccountsSelection = "Select client accounts",
  ConfirmAndAddToCart = "Confirm and add to cart"
}

export enum CheckoutStepEnum {
  Address = "Address",
  Shipping = "Shipping",
  ReviewAndPayment = "Review and Payment",
  Completed = "Completed"
}

export enum StepTypeEnum {
  Checkout = "Checkout"
}
